import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ODataModel } from '../../interfaces/odata-model.interface';
import { TreeViewNode } from '../../models/primitive/tree-view-node';
import { TreeViewModel } from '../../models/primitive/tree-view.model';
import { TreeComponent } from '../tree/tree.component';

@Component({
    selector: 'pace-core-tree-view',
    templateUrl: './tree-view.component.html',
    styleUrls: ['./tree-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeViewComponent implements OnInit, OnDestroy {
    @ViewChild('treeComponent')
    public treeComponent: TreeComponent;

    @Input()
    public model: TreeViewModel<ODataModel>;

    @Output()
    public modelChange = new EventEmitter<TreeViewModel<ODataModel>>();

    @Input()
    public parentForm: FormArray;

    @Output()
    public valueChange = new EventEmitter<void>();

    public treeViewForm: FormGroup;
    public treeViewFormChildren: FormArray;
    public firstLevelNodes: number[] = [];
    public faCheck = faCheck;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        // disable selection on non-leaf nodes
        let nodesToCheck: Array<TreeViewNode<ODataModel>> = [];
        nodesToCheck.push(this.model.value);
        while (nodesToCheck.length > 0) {
            const currentNode = nodesToCheck.shift();
            if (currentNode) {
                currentNode.hasChildren = currentNode.children.length > 0;
                nodesToCheck = nodesToCheck.concat(currentNode.children);
            }
        }

        // Stores the list of 1st children of the Tree. Used in collapseAll() function to collapse the entire tree.
        for (const child of this.model.value.children) {
            this.firstLevelNodes.push(child.id);
        }
        const treeViewValidator = (): ValidationErrors | null => this.checkValidity();
        this.treeViewFormChildren = this.formBuilder.array([]);
        this.treeViewForm = this.formBuilder.group(
            {
                children: this.treeViewFormChildren,
            },
            {
                validators: treeViewValidator,
            },
        );

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.treeViewForm);
                })
                .catch((error: Error) =>
                    this.log.error(
                        `TreeViewComponent: ngOnInit: parentForm.push: ${JSON.stringify(
                            error,
                            Object.getOwnPropertyNames(error),
                        )}`,
                    ),
                );
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.treeViewForm);
            this.parentForm.removeAt(index);
        }
    }

    /**
     * Callback to update the model and validation when the selection of a leaf node changes
     *
     * @param selectedNode The selected node
     */
    public onSelect(selectedNode: TreeViewNode<ODataModel>): void {
        // if the treeViewNode is not a leaf, it should not be selectable. Instead, we expand/collapse the children
        if (!selectedNode.hasChildren) {
            const selectedChildrenCount = this.model.value.selectedChildren.length;
            selectedNode.selected = this.model.selectEntryByIdAndTitle(selectedNode.id, selectedNode.value);

            this.treeViewForm.updateValueAndValidity();
            if (this.model.value.selectedChildren.length !== selectedChildrenCount) {
                this.modelChange.emit(this.model);
                this.valueChange.emit();
            }
        } else {
            if (selectedNode.isExpanded) {
                selectedNode.collapse();
            } else {
                selectedNode.expand();
            }
        }
    }

    /**
     * Check the validity of the tree under consideration of its model options
     *
     * @returns null if valid, else a ValidationErrors object
     */
    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        // check required: is at least one node selected?
        if (this.model && this.model.options.required && this.model.selectedEntries.length === 0) {
            errors.required = 'No selection in tree view';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
